import { Controller } from "@hotwired/stimulus";
import {
  captureFilterState, applyFilterState, StaleFilterError, snapshotSelections, restoreSelections,
  snapshotAsState, selectionsDiffer, describeFilterState, filterStateChanges,
  UnsavedDashboardError, filterIds, unsavedFilterChange
} from "../helpers/reveal_saved_views";
import { captureRevealError } from "../helpers/reveal_sentry";

const DEFAULT_LABEL = "Default (as published)";
const DUPLICATE_NAME = "You already have a set with that name.";
const REFUSALS = {
  400: "this dashboard's filter choices can't be stored as a set",
  403: "filter sets aren't available here",
  404: "this dashboard is no longer available"
};
const atStep = (step, error) => Object.assign(error instanceof Error ? error : new Error(String(error)), { step });

const element = (tag, className = "", text = "") => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};

export default class extends Controller {
  static outlets = ["reveal-bi-embed"];
  static targets = ["toggle", "menu", "modified", "update", "form", "name", "sharing", "share", "status", "formStatus", "manage", "rows"];

  connect() {
    this.live = true;
    this.abortController = new AbortController();
    this.generation = 0;
    this.views = [];
    this.hiddenIds = [];
    this.requestedView = new URLSearchParams(window.location.search).get("view");
    this.onReady = event => {
      if (this.hasRevealBiEmbedOutlet && event.target === this.revealBiEmbedOutlet.element) this.contextChanged();
    };
    this.onSaved = event => {
      if (this.hasRevealBiEmbedOutlet && event.target === this.revealBiEmbedOutlet.element) this.dashboardSaved();
    };
    document.addEventListener("reveal-bi-embed:ready", this.onReady);
    document.addEventListener("reveal-bi-embed:token-refreshed", this.onReady);
    document.addEventListener("reveal-bi-embed:saved", this.onSaved);
    if (this.hasRevealBiEmbedOutlet && this.revealBiEmbedOutlet.getDashboard()) this.ready();
  }

  revealBiEmbedOutletConnected(embed) {
    if (this.live && embed.getDashboard()) this.contextChanged();
  }

  revealBiEmbedOutletDisconnected() {
    if (this.live) this.contextChanged();
  }

  contextChanged() {
    this.generation += 1;
    this.abortController.abort();
    this.abortController = new AbortController();
    this.views = [];
    this.hiddenIds = [];
    this.canShare = false;
    this.sharingTarget.hidden = true;
    this.renderMenu();
    this.rowsTarget.replaceChildren();
    this.cancel();
    this.message("");
    this.toggleButtons(true);
    this.reloadPending = true;
    if (!this.busy) this.ready();
  }

  captureContext() {
    const embed = this.revealBiEmbedOutlet;
    return { embed, dashboard: embed.getDashboard(), token: embed.tokenValue,
      dashboardId: embed.dashboardIdValue, serverUrl: embed.serverUrlValue,
      generation: this.generation, signal: this.abortController.signal };
  }

  isCurrent(context) {
    return this.live && this.hasRevealBiEmbedOutlet && this.generation === context.generation &&
      this.revealBiEmbedOutlet === context.embed && context.embed.getDashboard() === context.dashboard &&
      context.embed.tokenValue === context.token && context.embed.dashboardIdValue === context.dashboardId &&
      context.embed.serverUrlValue === context.serverUrl;
  }

  assertCurrent(context) {
    if (!this.isCurrent(context)) throw new Error("Dashboard context changed.");
  }

  async ready() {
    if (!this.live || this.busy || !this.hasRevealBiEmbedOutlet || !this.revealBiEmbedOutlet.getDashboard()) return;
    this.reloadPending = false;
    const context = this.captureContext();
    this.track(context);
    try {
      await this.load(context);
      this.assertCurrent(context);
      this.toggleButtons(false);
    } catch {
      if (this.isCurrent(context)) this.message("Filter sets unavailable.");
      return;
    }
    await this.applyRequested();
  }

  // The published state is snapshotted once per dashboard, before any set is applied.
  track(context) {
    if (this.baselineDashboard !== context.dashboard) {
      this.baselineDashboard = context.dashboard;
      this.baseline = snapshotSelections(context.dashboard);
      this.savedFilterIds = filterIds(context.dashboard);
      this.active = null;
      this.applied = snapshotAsState(this.baseline);
      this.showModified(false);
    }
    const view = context.embed.getRevealView?.();
    if (view && view !== this.watchedView) {
      const previous = view.onFilterValueChanged;
      view.onFilterValueChanged = args => {
        if (typeof previous === "function") previous(args);
        this.filtersChanged();
      };
      this.watchedView = view;
    }
  }

  dashboardSaved() {
    const dashboard = this.revealBiEmbedOutlet.getDashboard();
    if (dashboard) this.savedFilterIds = filterIds(dashboard);
    if (this.savingBlocked) this.unblockSaving();
    if (dashboard && dashboard === this.baselineDashboard) this.refreshHiddenDefaults();
  }

  // An author can move or hide a filter and save without reloading. Re-read which filters
  // are hidden and take their saved values, so applying a set doesn't restore the old ones.
  async refreshHiddenDefaults() {
    const context = this.captureContext();
    try {
      await this.load(context);
    } catch {
      return;
    }
    if (!this.isCurrent(context) || this.baselineDashboard !== context.dashboard) return;
    const hidden = new Set(this.hiddenIds);
    const saved = snapshotSelections(context.dashboard);
    this.baseline = this.baseline.map(entry =>
      (hidden.has(entry.filter.id) ? saved.find(fresh => fresh.filter.id === entry.filter.id) ?? entry : entry));
  }

  unblockSaving() {
    this.savingBlocked = false;
    if (this.refusal && this.shownMessage() === this.refusal) this.message("");
    this.refusal = null;
    if (!this.busy) this.toggleButtons(false);
  }

  syncSaveButtons() {
    if (!this.savingBlocked) return;
    this.formTarget.querySelectorAll("button:not([type=button])").forEach(button => { button.disabled = true; });
    this.updateTarget.disabled = true;
  }

  assertSavedFilters(context) {
    const change = unsavedFilterChange(context.dashboard, this.savedFilterIds ?? filterIds(context.dashboard));
    if (change) throw new UnsavedDashboardError(change);
  }

  filtersChanged() {
    if (!this.live) return;
    if (this.savingBlocked && this.hasRevealBiEmbedOutlet) {
      const dashboard = this.revealBiEmbedOutlet.getDashboard();
      if (dashboard && !unsavedFilterChange(dashboard, this.savedFilterIds ?? filterIds(dashboard))) this.unblockSaving();
    }
    clearTimeout(this.modifiedTimer);
    this.modifiedTimer = setTimeout(() => this.refreshModified(), 150);
  }

  refreshModified() {
    const dashboard = this.baselineDashboard;
    this.showModified(!!(this.live && dashboard && this.applied && selectionsDiffer(dashboard, this.applied, this.hiddenIds)));
  }

  showModified(modified) {
    this.modifiedTarget.hidden = !modified;
    this.updateTarget.hidden = !(modified && this.active?.is_owner);
    if (this.active) this.updateTarget.textContent = `Update "${this.active.name}"`;
  }

  async applyRequested() {
    const id = this.requestedView;
    if (!id) return;
    this.requestedView = null;
    if (this.views.some(view => view.id === id)) {
      await this.select(id);
    } else {
      this.setUrl(null);
      this.message("Filter set unavailable.");
    }
  }

  async request(path = "", options = {}, context = this.captureContext()) {
    this.assertCurrent(context);
    let response;
    try {
      response = await fetch(`${context.serverUrl}/dashboards/${encodeURIComponent(context.dashboardId)}/views${path}`, {
        ...options,
        headers: { "Authorization": `Bearer ${context.token}`, "Content-Type": "application/json" },
        signal: context.signal
      });
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      throw atStep("request", new Error("Filter set request did not reach Reveal."));
    }
    this.assertCurrent(context);
    if (!response.ok) throw Object.assign(new Error(`Filter set request failed: ${response.status}`), { status: response.status, step: "response" });
    const result = response.status === 204 ? null : await response.json();
    this.assertCurrent(context);
    return result;
  }

  async load(context = this.captureContext()) {
    const result = await this.request("", {}, context);
    this.assertCurrent(context);
    this.canShare = result.can_share_with_organization === true;
    this.sharingTarget.hidden = !this.canShare;
    if (!this.canShare) this.shareTarget.checked = false;
    this.views = result.views;
    this.hiddenIds = Array.isArray(result.hidden_filter_ids) ? result.hidden_filter_ids : [];
    if (this.active) {
      const current = this.views.find(view => view.id === this.active.id);
      // A deleted or unshared set leaves its filters in place: show them as modified from Default.
      if (current) this.active = current;
      else this.activate(null);
    }
    this.renderMenu();
    this.renderRows();
    this.refreshModified();
    return this.views;
  }

  renderMenu() {
    const mine = this.views.filter(view => view.is_owner);
    const shared = this.views.filter(view => !view.is_owner);
    const items = [this.menuItem(DEFAULT_LABEL, ""), this.menuHeader("Mine"),
      ...(mine.length ? mine.map(view => this.menuItem(view.name, view.id)) : [this.menuNote("No saved filter sets")])];
    if (this.canShare || shared.length) {
      items.push(this.menuHeader("Shared in your organization"),
        ...(shared.length ? shared.map(view => this.menuItem(view.name, view.id)) : [this.menuNote("None shared yet")]));
    }
    const divider = element("li");
    divider.append(element("hr", "dropdown-divider"));
    items.push(divider, this.menuAction("Save current filters as…", "showForm"), this.menuAction("Manage filter sets…", "openManage"));
    this.menuTarget.replaceChildren(...items);
    this.toggleTarget.textContent = this.active ? this.active.name : DEFAULT_LABEL;
  }

  menuItem(text, id) {
    const item = this.menuAction(text, "choose");
    const button = item.firstChild;
    button.dataset.viewId = id;
    if ((this.active?.id ?? "") === id) {
      button.classList.add("active");
      button.setAttribute("aria-current", "true");
    }
    return item;
  }

  menuAction(text, action) {
    const item = element("li");
    item.append(this.button(text, action, "dropdown-item"));
    return item;
  }

  menuHeader(text) {
    const item = element("li");
    item.append(element("h6", "dropdown-header", text));
    return item;
  }

  menuNote(text) {
    const item = element("li");
    item.append(element("span", "dropdown-item-text text-muted small", text));
    return item;
  }

  button(text, action, className = "btn btn-link btn-sm", id = null) {
    const button = element("button", className, text);
    button.type = "button";
    button.dataset.action = `reveal-saved-views#${action}`;
    if (id) button.dataset.viewId = id;
    button.disabled = !!this.busy;
    return button;
  }

  renderRows() {
    const dashboard = this.baselineDashboard;
    const rows = this.views.map(view => this.row(view, dashboard));
    this.rowsTarget.replaceChildren(...(rows.length ? rows : [element("li", "list-group-item text-muted", "No filter sets yet.")]));
  }

  row(view, dashboard) {
    const row = element("li", "list-group-item");
    row.dataset.viewId = view.id;
    const head = element("div", "d-flex flex-wrap align-items-center gap-2");
    head.append(this.nameField(view), this.visibilityField(view), element("small", "text-muted", this.updatedText(view)));
    const summary = describeFilterState(view.view_state, this.hiddenIds);
    const detail = element("div", "small text-truncate mt-1", summary);
    detail.title = summary;
    row.append(head, detail);
    const changes = dashboard ? filterStateChanges(view.view_state, dashboard, this.hiddenIds) : [];
    if (changes.length) row.append(element("div", "small text-danger", `Dashboard changed: ${changes.join("; ")}`));
    row.append(this.rowActions(view));
    return row;
  }

  nameField(view) {
    if (!view.is_owner) return element("span", "fw-semibold", view.name);
    const input = element("input", "form-control form-control-sm w-auto");
    Object.assign(input, { type: "text", value: view.name, maxLength: 255, required: true });
    input.setAttribute("aria-label", "Filter set name");
    input.dataset.action = "change->reveal-saved-views#rename";
    input.dataset.viewId = view.id;
    return input;
  }

  visibilityField(view) {
    if (!view.is_owner) return element("span", "badge text-bg-light", `Shared by ${view.owner_label === "You" ? "you" : "a colleague"}`);
    if (!this.canShare) return element("span", "badge text-bg-light", view.visibility === "organization" ? "Shared" : "Private");
    const wrapper = element("label", "form-check form-switch mb-0 small");
    const input = element("input", "form-check-input");
    Object.assign(input, { type: "checkbox", checked: view.visibility === "organization" });
    input.setAttribute("role", "switch");
    input.dataset.action = "change->reveal-saved-views#toggleShare";
    input.dataset.viewId = view.id;
    wrapper.append(input, document.createTextNode(" Shared with my organization"));
    return wrapper;
  }

  updatedText(view) {
    const updated = view.updated_at ? new Date(view.updated_at) : null;
    return updated && !Number.isNaN(updated.getTime())
      ? `Updated ${updated.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}` : "";
  }

  rowActions(view) {
    const actions = element("div", "d-flex flex-wrap align-items-center gap-1 mt-1");
    actions.append(this.button("Apply", "choose", "btn btn-outline-primary btn-sm", view.id));
    if (view.is_owner) {
      const confirm = element("span");
      confirm.hidden = true;
      confirm.dataset.confirmDelete = "";
      const prompt = element("span", "d-inline-flex align-items-center gap-1 small");
      prompt.append("Delete?", this.button("Yes", "confirmDelete", "btn btn-danger btn-sm", view.id),
        this.button("No", "cancelDelete", "btn btn-outline-secondary btn-sm", view.id));
      confirm.append(prompt);
      actions.append(this.button("Update from current", "updateRow", "btn btn-outline-secondary btn-sm", view.id),
        this.button("Delete", "askDelete", "btn btn-outline-danger btn-sm", view.id), confirm);
    }
    return actions;
  }

  closeMenu() {
    const Dropdown = window.bootstrap?.Dropdown;
    if (Dropdown) Dropdown.getOrCreateInstance(this.toggleTarget).hide();
    else {
      this.menuTarget.classList.remove("show");
      this.toggleTarget.classList.remove("show");
      this.toggleTarget.setAttribute("aria-expanded", "false");
    }
  }

  openManage() {
    this.closeMenu();
    this.renderRows();
    const Modal = window.bootstrap?.Modal;
    if (Modal) Modal.getOrCreateInstance(this.manageTarget).show();
    else this.manageTarget.classList.add("show", "d-block");
  }

  showForm() {
    this.closeMenu();
    this.formTarget.hidden = false;
    this.nameTarget.focus();
  }

  cancel() {
    this.formTarget.hidden = true;
    this.nameTarget.value = "";
    this.shareTarget.checked = false;
  }

  choose(event) {
    this.closeMenu();
    return this.select(event.currentTarget.dataset.viewId);
  }

  select(id) {
    if (!id) return this.restoreDefault();
    return this.perform(async context => {
      const view = (await this.load(context)).find(v => v.id === id);
      if (!view) throw new Error("Filter set unavailable.");
      await applyFilterState(context.dashboard, view.view_state, { isCurrent: () => this.isCurrent(context),
        hiddenIds: this.hiddenIds, defaults: this.baselineDashboard === context.dashboard ? snapshotAsState(this.baseline) : null });
      this.assertCurrent(context);
      context.embed.getRevealView().refreshDashboardData();
      this.activate(view);
      this.message(`Applied "${view.name}".`);
    }, error => this.applyFailure(error, id));
  }

  applyFailure(error, id) {
    if (error instanceof StaleFilterError) {
      return `Could not apply this filter set: ${error.filterTitle || "a filter"} no longer offers ${error.missingLabels.join(", ")}.`;
    }
    const view = this.views.find(v => v.id === id);
    if (!view) return "Filter set unavailable.";
    const changes = this.baselineDashboard ? filterStateChanges(view.view_state, this.baselineDashboard, this.hiddenIds) : [];
    return changes.length ? `Could not apply this filter set. Dashboard changed: ${changes.join("; ")}.` : "Could not apply this filter set.";
  }

  restoreDefault() {
    return this.perform(async context => {
      if (this.baselineDashboard !== context.dashboard) throw new Error("Dashboard context changed.");
      restoreSelections(this.baseline);
      context.embed.getRevealView().refreshDashboardData();
      this.activate(null);
      this.message("Showing the published default.");
    }, "Could not restore the published default.");
  }

  activate(view) {
    this.active = view;
    this.applied = view ? view.view_state : snapshotAsState(this.baseline);
    this.setUrl(view?.id);
    this.renderMenu();
    this.refreshModified();
  }

  setUrl(id) {
    const url = new URL(window.location.href);
    if (id) url.searchParams.set("view", id);
    else url.searchParams.delete("view");
    window.history.replaceState(window.history.state, "", url);
  }

  async save(event) {
    event.preventDefault();
    await this.perform(async context => {
      this.assertSavedFilters(context);
      const state = await captureFilterState(context.dashboard, this.hiddenIds).catch(error => { throw atStep("capture", error); });
      this.assertCurrent(context);
      const view = await this.request("", { method: "POST", body: JSON.stringify({
        name: this.nameTarget.value.trim(),
        visibility: this.canShare && this.shareTarget.checked ? "organization" : "private",
        view_state: state
      }) }, context);
      this.cancel();
      await this.load(context);
      this.activate(this.views.find(v => v.id === view.id) ?? view);
      this.message(`Saved "${view.name}".`);
    }, error => this.writeFailure(error, "save"));
  }

  writeFailure(error, verb) {
    if (error instanceof UnsavedDashboardError) {
      this.savingBlocked = true;
      this.refusal = `Save the dashboard first: ${error.change}.`;
      return this.refusal;
    }
    if (error.status === 409) return DUPLICATE_NAME;
    captureRevealError(`filter set ${verb}`, new Error(`step=${error.step || "other"} status=${error.status || "none"}`),
      { dashboardId: this.hasRevealBiEmbedOutlet ? this.revealBiEmbedOutlet.dashboardIdValue : "" });
    const failed = `Could not ${verb} this filter set`;
    if (error.step === "request") return `${failed}: Reveal didn't answer. Check your connection and try again.`;
    if (error.step === "capture") return `${failed}: a dashboard filter couldn't be read.`;
    if (REFUSALS[error.status]) return `${failed}: ${REFUSALS[error.status]}.`;
    return `${failed}. Try again.`;
  }

  updateCurrent() {
    if (this.active?.is_owner) return this.updateFromCurrent(this.active.id);
    return undefined;
  }

  updateRow(event) {
    return this.updateFromCurrent(event.currentTarget.dataset.viewId);
  }

  updateFromCurrent(id) {
    return this.perform(async context => {
      const view = this.views.find(v => v.id === id && v.is_owner);
      if (!view) throw new Error("Filter set unavailable.");
      this.assertSavedFilters(context);
      const state = await captureFilterState(context.dashboard, this.hiddenIds).catch(error => { throw atStep("capture", error); });
      this.assertCurrent(context);
      const updated = await this.request(`/${encodeURIComponent(id)}`, { method: "PUT",
        body: JSON.stringify({ name: view.name, visibility: view.visibility, view_state: state }) }, context);
      await this.load(context);
      this.activate(this.views.find(v => v.id === id) ?? updated);
      this.message(`Updated "${updated.name}".`);
    }, error => this.writeFailure(error, "update"));
  }

  rename(event) {
    const { viewId } = event.currentTarget.dataset;
    const name = event.currentTarget.value.trim();
    return this.patch(viewId, { name }, `Renamed to "${name}".`,
      error => (error.status === 409 ? DUPLICATE_NAME : "Could not rename this filter set."));
  }

  toggleShare(event) {
    const { viewId } = event.currentTarget.dataset;
    const shared = event.currentTarget.checked;
    return this.patch(viewId, { visibility: shared ? "organization" : "private" },
      shared ? "Shared with your organization." : "Now private.", "Could not change sharing.");
  }

  patch(id, body, success, failure) {
    return this.perform(async context => {
      try {
        await this.request(`/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }, context);
      } finally {
        if (this.isCurrent(context)) await this.load(context);
      }
      this.message(success);
    }, failure);
  }

  askDelete(event) {
    this.confirmFor(event.currentTarget).hidden = false;
  }

  cancelDelete(event) {
    this.confirmFor(event.currentTarget).hidden = true;
  }

  confirmFor(button) {
    return button.closest("[data-view-id]:not(button)").querySelector("[data-confirm-delete]");
  }

  confirmDelete(event) {
    const id = event.currentTarget.dataset.viewId;
    return this.perform(async context => {
      await this.request(`/${encodeURIComponent(id)}`, { method: "DELETE" }, context);
      await this.load(context);
      this.message("Filter set deleted.");
    }, "Could not delete this filter set.");
  }

  async perform(operation, failure) {
    if (this.busy || !this.live || !this.hasRevealBiEmbedOutlet) return;
    const context = this.captureContext();
    this.busy = true;
    this.toggleButtons(true);
    this.message("");
    try { await operation(context); } catch (error) {
      if (this.isCurrent(context)) this.message(typeof failure === "function" ? failure(error) : failure);
    } finally {
      this.busy = false;
      if (this.live && this.reloadPending) this.ready();
      else if (this.isCurrent(context)) this.toggleButtons(false);
    }
  }

  toggleButtons(disabled) {
    this.element.querySelectorAll("button").forEach(button => { button.disabled = disabled; });
    if (!disabled) this.syncSaveButtons();
  }

  message(text) {
    if (!this.live) return;
    const inline = this.hasFormStatusTarget && !this.formTarget.hidden;
    this.statusTarget.textContent = inline ? "" : text;
    if (this.hasFormStatusTarget) this.formStatusTarget.textContent = inline ? text : "";
  }

  shownMessage() {
    return (this.hasFormStatusTarget && this.formStatusTarget.textContent) || this.statusTarget.textContent;
  }

  disconnect() {
    this.live = false;
    clearTimeout(this.modifiedTimer);
    this.abortController.abort();
    document.removeEventListener("reveal-bi-embed:ready", this.onReady);
    document.removeEventListener("reveal-bi-embed:token-refreshed", this.onReady);
    document.removeEventListener("reveal-bi-embed:saved", this.onSaved);
  }
}
